/**
 * EmailTemplateModal (TMPL-01) — THE send surface for any rendered template:
 * statement (Collection Manager), invoice email (invoice page), and every
 * template after. One shape everywhere ([[feedback_declutter_best_in_class]]):
 * recipients resolve from the record (Pegasus contacts + ESC on-file emails,
 * unsubscribed hard-blocked), the iframe preview IS the exact html that
 * sends, and the send lands on the customer's message thread via send-email.
 *
 * The html comes from render-template + the pegasus_template row — when the
 * TMPL-02 editor changes a template, every consumer follows, zero code.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/lib/cn";
import { useEntryFormKeys } from "@/lib/keys";
import { supabase } from "@/lib/supabase";
import { useCustomerConsent, purposeForMessageType } from "@/features/comms/consent";
import { useContacts, type ContactRow } from "@/features/customer-360/CustomerContacts";

interface RenderResult {
  ok?: boolean;
  subject?: string;
  html?: string;
  meta?: Record<string, unknown> & { total_owed?: number; open_invoices?: number };
  error?: string;
  detail?: string;
}

export function EmailTemplateModal({
  custno,
  customerName,
  templateKey,
  templateParams,
  title,
  messageType,
  onClose,
}: {
  custno: string;
  customerName: string;
  /** pegasus_template key — 'statement', 'invoice_email', … */
  templateKey: string;
  /** Extra ids render-template needs (e.g. { invoice: "0000124187" }). */
  templateParams?: Record<string, string>;
  /** Modal title, e.g. "Email Statement — Armitage Homes". */
  title: string;
  /** Stamped on the message-thread row ('statement', 'invoice'…). */
  messageType: string;
  onClose: () => void;
}) {
  // 1) Render — the preview IS the send (same html).
  const render = useQuery({
    queryKey: ["render_template", templateKey, custno, templateParams],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<RenderResult> => {
      const { data, error } = await supabase.functions.invoke("render-template", {
        body: { template_key: templateKey, custno, ...(templateParams ?? {}) },
      });
      if (error) throw error;
      return (data ?? {}) as RenderResult;
    },
  });

  // 2) Recipients: Pegasus contacts with email + ESC on-file location emails.
  const contacts = (useContacts(custno, null).data ?? []).filter(
    (c) => c.email,
  ) as (ContactRow & { email: string })[];
  const { data: onFile } = useQuery({
    queryKey: ["loc_emails", custno],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("mirror_location")
        .select("email, email2")
        .eq("custno", custno);
      if (error) throw error;
      const set = new Set<string>();
      for (const r of (data ?? []) as { email: string | null; email2: string | null }[]) {
        for (const e of [r.email, r.email2]) {
          const v = (e ?? "").trim().toLowerCase();
          if (v && v.includes("@")) set.add(v);
        }
      }
      return [...set];
    },
  });

  // Previously-harvested one-off addresses (Pegasus-native, since ESC is read-only)
  // show as on-file chips too, so a saved address is reusable next time.
  const { data: harvested } = useQuery({
    queryKey: ["cust_emails", custno],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("pegasus_customer_email")
        .select("email")
        .eq("custno", custno);
      if (error) throw error;
      const set = new Set<string>();
      for (const r of (data ?? []) as { email: string | null }[]) {
        const v = (r.email ?? "").trim().toLowerCase();
        if (v.includes("@")) set.add(v);
      }
      return [...set];
    },
  });

  const allEmails = useMemo(() => {
    const s = new Set<string>(contacts.map((c) => c.email.toLowerCase()));
    for (const e of onFile ?? []) s.add(e);
    for (const e of harvested ?? []) s.add(e);
    return [...s];
  }, [contacts, onFile, harvested]);

  // CONSENT-01: blocked addresses come from the one consent model, judged for
  // THIS message's purpose (a statement is billing; a marketing opt-out
  // doesn't stop an invoice).
  const purpose = purposeForMessageType(messageType);
  const consent = useCustomerConsent(custno, allEmails.map((e) => ({ channel: "email" as const, endpoint: e })));
  const optedOut = useMemo(
    () => new Set(allEmails.filter((e) => consent.blocked("email", e, purpose)).map((e) => e.toLowerCase())),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allEmails, consent.rows, purpose],
  );

  const [checked, setChecked] = useState<Set<string>>(new Set());
  // Pre-check the first sendable address once candidates resolve.
  useEffect(() => {
    if (checked.size > 0) return;
    const first = allEmails.find((e) => !optedOut.has(e));
    if (first) setChecked(new Set([first]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEmails.length, optedOut.size]);

  const toggle = (email: string) => {
    const v = email.toLowerCase();
    if (optedOut.has(v)) return;
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  };

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // One-off recipient — an address not on file (customer asked us to send a copy
  // elsewhere). Optionally harvested to the record, mirroring the invoice builder.
  const [extra, setExtra] = useState("");
  const [saveExtra, setSaveExtra] = useState(false);
  const extraNorm = extra.trim().toLowerCase();
  const extraValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(extraNorm) && !optedOut.has(extraNorm);
  const extraBlocked = extraNorm.length > 0 && optedOut.has(extraNorm);
  const recipients = useMemo(() => {
    const s = new Set<string>(checked);
    if (extraValid) s.add(extraNorm);
    return [...s];
  }, [checked, extraValid, extraNorm]);

  const r = render.data;
  const canSend = Boolean(r?.ok && r.html && recipients.length > 0 && !sending && !sent);

  const onFormKeys = useEntryFormKeys(() => { if (canSend) void send(); });
  const send = async () => {
    if (!r?.html) return;
    setSending(true);
    setErr(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          to: recipients,
          subject: r.subject ?? `A message from Sedgwick Heating`,
          html: r.html,
          text: `${title} — view this email in an HTML-capable mail app. Questions? Call 952-881-9000.`,
          custno,
          message_type: messageType,
        },
      });
      const detail = (data as { error?: string } | null) ?? null;
      if (error || detail?.error) {
        setErr(error?.message ?? detail?.error ?? "send failed");
        return;
      }
      // Save the typed one-off address to the record (propose→confirm) so it's reusable.
      if (saveExtra && extraValid) {
        try {
          const { data: u } = await supabase.auth.getUser();
          await supabase.from("pegasus_customer_email").insert({
            custno, email: extraNorm, source: "invoice", created_by: u?.user?.email ?? null,
          });
        } catch (e) { console.error("email harvest failed:", e); }
      }
      setSent(true);
      window.setTimeout(onClose, 1200);
    } catch (e) {
      setErr(String(e));
    } finally {
      setSending(false);
    }
  };

  const contactByEmail = new Map(contacts.map((c) => [c.email.toLowerCase(), c]));
  const metaOwed = typeof r?.meta?.total_owed === "number" ? r.meta.total_owed : null;
  const metaCount = typeof r?.meta?.open_invoices === "number" ? r.meta.open_invoices : null;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={title}
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-400">
            Sends as Sedgwick Heating &lt;service@sedgwickheating.com&gt; and
            lands on this customer&apos;s thread.
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={send}
              disabled={!canSend}
              className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {sent ? "Sent ✓" : sending ? "Sending…" : `Send to ${recipients.length || "…"}`}
            </button>
          </span>
        </div>
      }
    >
      <div className="space-y-3" data-entry-form onKeyDown={onFormKeys}>
        {/* Recipients */}
        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Who gets it
          </span>
          {allEmails.length === 0 ? (
            <p className="rounded-md border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400">
              No email on this account — add a contact with an email first
              (their record → Contacts).
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {allEmails.map((e) => {
                const blocked = optedOut.has(e);
                const c = contactByEmail.get(e);
                return (
                  <label
                    key={e}
                    className={cn(
                      "flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
                      blocked
                        ? "cursor-not-allowed border-red-100 bg-red-50/60 text-red-400"
                        : checked.has(e)
                          ? "border-brand-300 bg-brand-50 text-brand-800"
                          : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
                    )}
                    title={blocked ? "Unsubscribed — Pegasus won't send here" : e}
                  >
                    <input
                      type="checkbox"
                      checked={checked.has(e)}
                      disabled={blocked}
                      onChange={() => toggle(e)}
                      className="h-3 w-3 accent-brand-700"
                    />
                    {c?.name ? `${c.name} · ` : ""}
                    {e}
                  </label>
                );
              })}
            </div>
          )}

          {/* One-off recipient — send a copy to an address not on file. */}
          <div className="mt-2 space-y-1.5">
            <input
              type="email"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="Also send to a different address…"
              className={cn(
                "w-full rounded-md border bg-white px-2.5 py-1.5 text-xs",
                extraBlocked ? "border-red-200" : "border-hairline",
              )}
            />
            {extraBlocked && (
              <p className="text-[11px] text-red-500">That address is unsubscribed — Pegasus won&apos;t send there.</p>
            )}
            {extraValid && (
              <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-slate-500">
                <input
                  type="checkbox"
                  checked={saveExtra}
                  onChange={(e) => setSaveExtra(e.target.checked)}
                  className="h-3 w-3 accent-brand-700"
                />
                Save this address to {customerName}&apos;s record for next time
              </label>
            )}
          </div>
        </div>

        {/* Preview — exactly what the customer receives. */}
        <div>
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Preview
            {metaOwed !== null && metaCount !== null && (
              <span className="ml-2 font-normal normal-case text-slate-400">
                {metaCount} open invoice{metaCount === 1 ? "" : "s"} · total {money(metaOwed)}
              </span>
            )}
          </span>
          {render.isLoading ? (
            <div className="flex h-72 items-center justify-center rounded-lg border border-hairline bg-slate-50 text-xs text-slate-400">
              Building the email…
            </div>
          ) : r?.ok && r.html ? (
            <iframe
              title="Email preview"
              srcDoc={r.html}
              sandbox="allow-same-origin"
              className="h-96 w-full rounded-lg border border-hairline bg-white"
            />
          ) : (
            <div className="flex h-40 items-center justify-center rounded-lg border border-red-100 bg-red-50/50 px-4 text-center text-xs text-red-600">
              <span>
                <Mail className="mx-auto mb-1 h-4 w-4" />
                Couldn&apos;t build the email: {r?.detail ?? r?.error ?? "unknown error"}
              </span>
            </div>
          )}
        </div>

        {err && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
            {err}
          </p>
        )}
      </div>
    </Modal>
  );
}

function money(v: number | undefined | null): string {
  return (v ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
}
